/**
 * debounce
 *
 * Returns a trailing-edge debounced version of `fn` that fires at most once
 * every `wait` milliseconds (default 16 ms — one animation frame).
 *
 * The returned function is generic: it accepts any argument list and forwards
 * them to the original function unchanged.  `this` is also forwarded, so the
 * helper works correctly when used as an object method.
 *
 * Usage:
 *   const debouncedHandler = debounce(myScrollHandler);          // 16 ms
 *   const debouncedHandler = debounce(myScrollHandler, 100);     // 100 ms
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  wait = 16,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return function (this: unknown, ...args: Parameters<T>) {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, wait);
  };
}
